import React, { useContext, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material';
import clsx from 'clsx';
import { omit } from 'lodash-es';
import { FormItemLabel } from './FormItemLabel';
import { FormItemInput } from './FormItemInput';
import { FormContext } from './context';
import { getFieldId, getValidateRules } from './utils';
import { getStyle, formClasses } from './style';
import { HOOK_MARK } from './constants';
import { Grid2 } from '../../Mui/Grid2';
const MemoInput = React.memo(
// eslint-disable-next-line react/prop-types
(props) => props.children, (prev, next) => prev.value === next.value &&
    prev.update === next.update &&
    prev.childProps.length === next.childProps.length &&
    prev.childProps.every((value, index) => value === next.childProps[index]));
export function FormItemBase(props) {
    const { children, label, labelExtra, description, labelWidth, labelClassName, labelAlign, labelCol, wrapperCol, name: propsName = '', required, colon, rules, noStyle, hidden, tooltip, extra, valuePropName, className, normalize, requiredMark, validateStatus, help, } = props;
    const { validateMessages, form, name: formName } = useContext(FormContext);
    const { formState, getFieldState } = useFormContext();
    const name = propsName;
    const fieldId = getFieldId(propsName, formName);
    const fieldErrors = useMemo(() => {
        if (help) {
            return [help];
        }
        const { error } = getFieldState(name, formState);
        if (!error) {
            return [];
        }
        return [error.message];
    }, [formState, getFieldState, help, name]);
    let mergedValidateStatus = '';
    if (validateStatus !== undefined) {
        mergedValidateStatus = validateStatus;
    }
    else if (fieldErrors.length > 0) {
        mergedValidateStatus = 'error';
    }
    const cls = clsx(className, formClasses.item.root, {
        [`${formClasses.item.withHelp}`]: fieldErrors.length > 0,
        [`${formClasses.item.hasError}`]: mergedValidateStatus === 'error',
        [`${formClasses.item.hidden}`]: hidden,
    });
    const { disabled } = useContext(FormContext);
    const wrapRules = useMemo(() => getValidateRules(rules ?? [], { label, name, validateMessages }), [label, name, rules, validateMessages]);
    const isRequired = 
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    required !== undefined
        ? required
        : !!rules?.some((rule) => {
            if (rule && typeof rule === 'object' && rule.required) {
                return true;
            }
            return false;
        });
    let childNode = null;
    const getChildNode = (field) => {
        if (React.isValidElement(children)) {
            const { defaultValue, ...restChildrenProps } = children.props;
            if (defaultValue !== undefined) {
                console.warn('Warning: [Form.Item] `defaultValue` will not work on controlled Field. You should use `initialValues` of Form instead.');
            }
            const mergedValuePropName = valuePropName ?? 'value';
            const childProps = {
                ...restChildrenProps,
                ...omit(field || {}, ['value', 'ref']),
                [mergedValuePropName]: field?.value,
                disabled: restChildrenProps.disabled ?? disabled,
                status: mergedValidateStatus,
                onChange: (...args) => {
                    if (normalize) {
                        field?.onChange(normalize(...args));
                    }
                    else {
                        field?.onChange(...args);
                    }
                    children.props.onChange?.(...args);
                },
            };
            if (!childProps.id) {
                childProps.id = fieldId;
            }
            if (description) {
                childProps['aria-describedby'] = clsx(childProps['aria-describedby'], `${fieldId}-desc`);
            }
            if (extra && name) {
                childProps['aria-describedby'] = clsx(childProps['aria-describedby'], `${fieldId}_extra`);
            }
            if (fieldErrors.length > 0) {
                childProps['aria-invalid'] = 'true';
                childProps['aria-errormessage'] = `${fieldId}_errormessage`;
            }
            if (isRequired) {
                childProps['aria-required'] = 'true';
            }
            const watchingChildProps = [
                childProps['aria-required'],
                childProps['aria-invalid'],
                childProps['aria-describedby'],
                childProps['aria-errormessage'],
            ];
            return field ? (React.createElement(MemoInput, { value: field.value, update: children, childProps: watchingChildProps }, React.cloneElement(children, childProps))) : (React.cloneElement(children, childProps));
        }
        if (typeof children === 'function') {
            return React.createElement(React.Fragment, null, children(form));
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return React.createElement(React.Fragment, null, children);
    };
    const renderLayout = (baseChildren) => (React.createElement("div", { className: cls },
        React.createElement(Grid2, { container: true, className: `${formClasses.item.root}-grid` },
            React.createElement(FormItemLabel, { label: label, labelWidth: labelWidth, className: labelClassName, required: isRequired, requiredMark: requiredMark, labelCol: labelCol, labelAlign: labelAlign, colon: colon, htmlFor: fieldId, tooltip: tooltip, labelExtra: labelExtra, description: description }),
            React.createElement(FormItemInput, { errors: fieldErrors, extra: extra, fieldId: fieldId, wrapperCol: wrapperCol }, baseChildren))));
    if (!name) {
        return renderLayout(getChildNode());
    }
    if (Array.isArray(children) && name) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn('Warning: A `Form.Item` with a `name` prop must have a single child element.');
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return React.createElement(React.Fragment, null, children);
        }
    }
    childNode = (React.createElement(Controller, { rules: wrapRules, name: name, control: form._getInternalHooks(HOOK_MARK)?.rhfForm.control, render: ({ field }) => getChildNode(field) }));
    if (noStyle && !hidden) {
        return childNode;
    }
    return renderLayout(childNode);
}
const StyledFormItem = styled(FormItemBase)((props) => {
    const { theme } = props;
    return getStyle(theme);
});
export const FormItem = React.forwardRef((props, ref) => (React.createElement(StyledFormItem, { ...props, ref: ref })));
